import { httpService } from '@/services/httpService';
import { log } from '@/utils/DebugLogger';
import { config } from '@/services/configService';
import { validate } from '../contracts/validate';
import type { Usuarios_CriaUsuarioInput } from '../contracts/Usuarios_CriaUsuarioInput';
import type { Usuarios_SalvarInfosInput } from '../contracts/Usuarios_SalvarInfosInput';
import type { Usuarios_AlterarSenhaUsuarioInput } from '../contracts/Usuarios_AlterarSenhaUsuarioInput';
import type { Usuarios_DeletaUsuarioInput } from '../contracts/Usuarios_DeletaUsuarioInput';
import '../contracts/Usuarios_CriaUsuarioInput.ts';
import '../contracts/Usuarios_AlterarSenhaUsuarioInput.ts';
import '../contracts/Usuarios_ListAvailableGroupsResponse';
import '../contracts/Usuarios_PegapermissoesusuarioResponse';
import '../contracts/Usuarios_PegausuariosResponse';
import '../contracts/Usuarios_SalvarinfosResponse';
import '../contracts/Usuarios_TogglefranquiausuarioResponse';
import '../contracts/Usuarios_TogglegrupousuarioResponse';
import '../contracts/Usuarios_DeletaUsuarioInput';
import '../contracts/Usuarios_SalvarInfosInput';
import '../contracts/Usuarios_HabilitaUsuarioInput';
import '../contracts/Usuarios_DesabilitaUsuarioInput';
import '../contracts/Usuarios_ToggleGrupoUsuarioInput';
import '../contracts/Usuarios_ToggleFranquiaUsuarioInput';
import '../contracts/Usuarios_DeletausuarioResponse';

import '../contracts/Usuarios_AlterarSenhaUsuarioResponse';
import '../contracts/Usuarios_CriaUsuarioResponse';
import '../contracts/Usuarios_DesabilitausuarioResponse';
import '../contracts/Usuarios_HabilitausuarioResponse';
import '../contracts/Usuarios_UploadimagemperfilResponse';
import '../contracts/Usuarios_UploadImagemPerfilInput';
/**
 * Composable for managing users via AWS Cognito
 * All operations interact with the Cognito API through CodeIgniter controller
 */
export function useUsers() {
  /**
   * Fetch all users from Cognito
   * @returns {Promise<Array>} List of users
   */
  const fetchUsers = async () => {
    log('useUsers', 'fetchUsers', 'Fetching users from Cognito');
    try {
      const response = await httpService.get(config.api.sistemaHost + '/usuarios/pegaUsuarios', {
        responseContract: 'Usuarios_PegausuariosResponse'
      });
      log('useUsers', 'fetchUsers', 'Raw response', response);
      
      // The API returns users as an object with user IDs as keys
      // Convert to array for the table
      let users = response.dados || {};
      
      if (typeof users === 'object' && !Array.isArray(users)) {
        users = Object.values(users);
      }
      
      log('useUsers', 'fetchUsers', 'Users loaded as array', users);
      return users || [];
    } catch (error) {
      log('useUsers', 'fetchUsers', 'Error loading users', error);
      throw error;
    }
  };

  /**
   * Fetch available user groups
   * @returns {Promise<Array>} List of groups
   */
  const fetchUserGroups = async (): Promise<string[]> => {
    log('useUsers', 'fetchUserGroups', 'Fetching user groups');
    try {
      const response = await httpService.get<{ dados?: { items?: string[] } | string[] }>(
        '/usuarios/list_available_groups',
        { responseContract: 'Usuarios_ListAvailableGroupsResponse' }
      );
      // Contract wraps the list under `dados.items` (Usuarios_ListAvailableGroupsResponse).
      const dados = response.dados;
      const groups: string[] = Array.isArray(dados)
        ? dados
        : (dados?.items ?? []);
      log('useUsers', 'fetchUserGroups', 'Groups loaded', groups);
      return groups;
    } catch (error) {
      log('useUsers', 'fetchUserGroups', 'Error loading groups', error);
      throw error;
    }
  };

  /**
   * Fetch a single user by their Cognito sub (ID)
   * @param {string} sub - User's Cognito subject ID
   * @returns {Promise<Object>} User data
   */
  const fetchUserBySub = async (sub: string) => {
    log('useUsers', 'fetchUserBySub', 'Fetching user by sub', sub);
    try {
      const response = await httpService.get<{
        dados?: { attributes?: Record<string, unknown> } & Record<string, unknown>
      }>(config.api.sistemaHost + `/usuarios/pegaUsuarioPorSub/${sub}`);
      // Usuarios_PegausuarioporsubResponse: top-level fields are
      // sub/username/email/enabled/phone_number/name; everything else
      // (documento, language, versaoSistema, bgcolor, ...) is nested
      // under `attributes`. Flatten so consumers see one object.
      const dados = response.dados ?? {};
      const { attributes, ...top } = dados;
      const user = { ...(attributes ?? {}), ...top };
      log('useUsers', 'fetchUserBySub', 'User loaded', user);
      return user;
    } catch (error) {
      log('useUsers', 'fetchUserBySub', 'Error loading user', error);
      throw error;
    }
  };

  /**
   * Fetch user permissions (groups and franchises)
   * @param {string} sub - User's Cognito subject ID
   * @returns {Promise<Object>} User permissions {groups: [], franquias: []}
   */
  const fetchUserPermissions = async (sub: string) => {
    log('useUsers', 'fetchUserPermissions', 'Fetching permissions for user', sub);
    try {
      const response = await httpService.get(`/usuarios/pegaPermissoesUsuario/${sub}`, {
        responseContract: 'Usuarios_PegapermissoesusuarioResponse'
      });
      // Surface backend failures instead of papering over them with empty data.
      // The legacy `response.dados || { groups: [], franquias: [] }` fallback hid
      // a real bug for months: pegaPermissoesUsuario was failing with
      // success:false for Full-only users, and the wizard treated that as
      // "user has no groups" → Próximo got stuck.
      if (response?.success === false) {
        throw new Error(
          typeof response.error === 'string' && response.error.length > 0
            ? response.error
            : 'Failed to retrieve permissions'
        );
      }
      const permissions = response.dados || { Groups: [], Franquias: {} };
      log('useUsers', 'fetchUserPermissions', 'Permissions loaded', permissions);
      return permissions;
    } catch (error) {
      log('useUsers', 'fetchUserPermissions', 'Error loading permissions', error);
      throw error;
    }
  };

  /**
   * Create new user in Cognito
   * @param {Object} data - User data {username, name, email, password, phone_number, language, bgcolor, documento}
   * @returns {Promise<Object>} Created user data
   */
  const createUser = async (data: Usuarios_CriaUsuarioInput) => {
    log('useUsers', 'createUser', 'Creating user', data);
    try {
      // Validate input before transformation
      validate('Usuarios_CriaUsuarioInput', data);

      // Send flat data — backend InputContract handles Cognito transformation
      const payload = {
        username: data.username,
        password: data.password,
        name: data.name,
        documento: data.documento,
        email: data.email,
        phone_number: data.phone_number,
        language: data.language,
        bgcolor: data.bgcolor,
        mcp_scopes: data.mcp_scopes
      };

      log('useUsers', 'createUser', 'Payload', payload);
      const response = await httpService.post('/usuarios/criaUsuario', payload, { responseContract: 'Usuarios_CriaUsuarioResponse', inputContract: 'Usuarios_CriaUsuarioInput' });

      // Check if operation was successful
      if (response.success === false) {
        const errorMessage = response.error || 'Unknown error occurred';
        log('useUsers', 'createUser', 'API returned error', errorMessage);
        throw new Error(errorMessage);
      }

      const result = response.dados;
      log('useUsers', 'createUser', 'User created', result);
      return result;
    } catch (error) {
      log('useUsers', 'createUser', 'Error creating user', error);
      throw error;
    }
  };

  /**
   * Update user information.
   * `data.username` is the Cognito Username (e.g. "testuser"), NOT the sub —
   * auth-service's UpdateUser → AdminGetUser only accepts the actual username.
   */
  const updateUser = async (data: Usuarios_SalvarInfosInput) => {
    log('useUsers', 'updateUser', 'Updating user', data);
    try {
      validate('Usuarios_SalvarInfosInput', data);
      const response = await httpService.post('/usuarios/salvarInfos', data, {
        responseContract: 'Usuarios_SalvarinfosResponse',
        inputContract: 'Usuarios_SalvarInfosInput'
      });
      if (response.success === false) {
        const errorMessage = response.error || 'Unknown error occurred';
        log('useUsers', 'updateUser', 'API returned error', errorMessage);
        throw new Error(errorMessage);
      }
      const result = response.dados;
      log('useUsers', 'updateUser', 'User updated', result);
      return result;
    } catch (error) {
      log('useUsers', 'updateUser', 'Error updating user', error);
      throw error;
    }
  };

  /**
   * Enable user account
   * @param {string} username - Username
   * @returns {Promise<Object>} Response data
   */
  const enableUser = async (username: string) => {
    log('useUsers', 'enableUser', 'Enabling user', username);
    try {
      const response = await httpService.get(`/usuarios/habilitaUsuario/${username}`, { responseContract: 'Usuarios_HabilitausuarioResponse' });
      const result = response.dados;
      log('useUsers', 'enableUser', 'User enabled', result);
      return result;
    } catch (error) {
      log('useUsers', 'enableUser', 'Error enabling user', error);
      throw error;
    }
  };

  /**
   * Disable user account
   * @param {string} sub - User's Cognito subject ID
   * @returns {Promise<Object>} Response data
   */
  const disableUser = async (sub: string) => {
    log('useUsers', 'disableUser', 'Disabling user', sub);
    try {
      const response = await httpService.get(`/usuarios/desabilitaUsuario/${sub}`, { responseContract: 'Usuarios_DesabilitausuarioResponse' });
      const result = response.dados;
      log('useUsers', 'disableUser', 'User disabled', result);
      return result;
    } catch (error) {
      log('useUsers', 'disableUser', 'Error disabling user', error);
      throw error;
    }
  };

  /**
   * Change user password
   * @param {string} username - Username
   * @param {string} password - New password
   * @returns {Promise<Object>} Response data
   */
  const changePassword = async (username: string, password: string) => {
    log('useUsers', 'changePassword', 'Changing password for user', username);
    try {
      const payload: Usuarios_AlterarSenhaUsuarioInput = { username, password };
      validate('Usuarios_AlterarSenhaUsuarioInput', payload);

      const response = await httpService.post('/usuarios/alterarSenhaUsuario', payload, {
        responseContract: 'Usuarios_AlterarSenhaUsuarioResponse',
        inputContract: 'Usuarios_AlterarSenhaUsuarioInput'
      });
      const result = response.dados;
      log('useUsers', 'changePassword', 'Password changed', result);
      return result;
    } catch (error) {
      log('useUsers', 'changePassword', 'Error changing password', error);
      throw error;
    }
  };

  /**
   * Upload user profile image
   * @param {string} sub - User's Cognito subject ID
   * @param {File} file - Image file
   * @returns {Promise<Object>} Response with image URL
   */
  const uploadProfileImage = async (sub: string, file: File) => {
    log('useUsers', 'uploadProfileImage', 'Uploading profile image', { sub, file: file.name });
    try {
      const formData = new FormData();
      formData.append('inputImagem', file);

      const response = await httpService.post(`/usuarios/uploadImagemPerfil/${sub}`, formData, {
        responseContract: 'Usuarios_UploadimagemperfilResponse',
        inputContract: 'Usuarios_UploadImagemPerfilInput'
      });
      const result = response.dados;
      log('useUsers', 'uploadProfileImage', 'Image uploaded', result);
      return result;
    } catch (error) {
      log('useUsers', 'uploadProfileImage', 'Error uploading image', error);
      throw error;
    }
  };

  /**
   * Toggle user group membership
   * @param {string} username - Username
   * @param {string} grupo - Group name
   * @returns {Promise<Object>} Response data
   */
  const toggleUserGroup = async (username: string, grupo: string) => {
    log('useUsers', 'toggleUserGroup', 'Toggling group', { username, grupo });
    try {
      const response = await httpService.post('/usuarios/toggleGrupoUsuario', { username, grupo }, {
        responseContract: 'Usuarios_TogglegrupousuarioResponse',
        inputContract: 'Usuarios_ToggleGrupoUsuarioInput'
      });
      const result = response.dados;
      log('useUsers', 'toggleUserGroup', 'Group toggled', result);
      return result;
    } catch (error) {
      log('useUsers', 'toggleUserGroup', 'Error toggling group', error);
      throw error;
    }
  };

  /**
   * Permanently delete a user from Cognito.
   */
  const deleteUser = async (sub: string) => {
    log('useUsers', 'deleteUser', 'Deleting user', sub);
    try {
      const payload: Usuarios_DeletaUsuarioInput = { sub };
      validate('Usuarios_DeletaUsuarioInput', payload);
      const response = await httpService.post('/usuarios/deletaUsuario', payload, {
        responseContract: 'Usuarios_DeletausuarioResponse',
        inputContract: 'Usuarios_DeletaUsuarioInput'
      });
      const result = response.dados;
      log('useUsers', 'deleteUser', 'User deleted', result);
      return result;
    } catch (error) {
      log('useUsers', 'deleteUser', 'Error deleting user', error);
      throw error;
    }
  };

  /**
   * Toggle user franchise access
   * @param {string} sub - User's Cognito subject ID
   * @param {number} franquiaID - Franchise ID
   * @returns {Promise<Object>} Response data
   */
  const toggleUserFranquia = async (sub: string, franquiaID: number) => {
    log('useUsers', 'toggleUserFranquia', 'Toggling franchise', { sub, franquiaID });
    try {
      const response = await httpService.post('/usuarios/toggleFranquiaUsuario', { sub, franquiaId: franquiaID }, {
        responseContract: 'Usuarios_TogglefranquiausuarioResponse',
        inputContract: 'Usuarios_ToggleFranquiaUsuarioInput'
      });
      const result = response.dados;
      log('useUsers', 'toggleUserFranquia', 'Franchise toggled', result);
      return result;
    } catch (error) {
      log('useUsers', 'toggleUserFranquia', 'Error toggling franchise', error);
      throw error;
    }
  };

  return {
    fetchUsers,
    fetchUserBySub,
    fetchUserGroups,
    fetchUserPermissions,
    createUser,
    updateUser,
    enableUser,
    disableUser,
    deleteUser,
    changePassword,
    uploadProfileImage,
    toggleUserGroup,
    toggleUserFranquia
  };
}
